/**
 * The chat session for the signed-in app: created once per trainer, started (its one hub
 * connection opened) for as long as the shell stands, and shared by every chat surface — the
 * navigation's dot, the inbox, a thread, the Zadania panel — so a send begun in the panel is the
 * same send on the full screen.
 */

import { useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useMemo, useSyncExternalStore, type ReactNode } from 'react';

import { createChatSession, type ChatSession, type RealtimeStatus } from '../model/chatSession';

import { productionPorts } from './chatAdapters';

const SessionContext = createContext<ChatSession | null>(null);

export function ChatSessionProvider({ userId, children }: { readonly userId: string | null; readonly children: ReactNode }) {
  const queryClient = useQueryClient();
  const session = useMemo(
    () => (userId === null ? null : createChatSession(productionPorts(queryClient), { userId })),
    [userId, queryClient],
  );
  useEffect(() => (session === null ? undefined : session.start()), [session]);
  return <SessionContext.Provider value={session}>{children}</SessionContext.Provider>;
}

export function useChatSession(): ChatSession {
  const session = useContext(SessionContext);
  if (session === null) throw new Error('A chat surface rendered outside the signed-in shell');
  return session;
}

/** Re-renders the caller whenever the session changes anything it could draw. */
export function useSessionVersion(session: ChatSession): number {
  return useSyncExternalStore(session.subscribe, session.version, session.version);
}

export function useRealtimeStatus(): RealtimeStatus {
  const session = useContext(SessionContext);
  const subscribe = useMemo(() => session?.subscribe ?? (() => () => undefined), [session]);
  const read = () => session?.status() ?? 'idle';
  return useSyncExternalStore(subscribe, read, read);
}

/** Whether the screens should say the connection is being re-established. */
export function isReconnecting(status: RealtimeStatus): boolean {
  return status === 'reconnecting';
}
